import algoliasearch from 'algoliasearch/lite'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import { Configure, InstantSearch } from 'react-instantsearch'
import { isEmpty } from 'remeda'
import type { MultipleQueriesQuery } from '@algolia/client-search'
import { $settings } from '@velasca/utils/globals'
import { handleize } from '@velasca/utils/handleize'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'
import { AnalyticsMiddleware } from './AnalyticsMiddleware'

interface FiltersContextProps {
  /**
   * What panel is currently opened. It's an unique ID for filter panels.
   */
  openPanel: string | null
  /**
   * Whether the search has been user activated.
   */
  isSearchActive: boolean
  isMenuOpen: boolean
  /**
   * Whether the search is loading on page load.
   */
  searchOnPageLoad: boolean
  setIsSearchActive: React.Dispatch<React.SetStateAction<boolean>>
  setPanel: (handle: string) => void
  resetPanel: () => void
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
  getHexColor: (colorName: string) => string | undefined
}

// Se c'è questo parametro nell'url significa che dovranno comparire i risultati di ricerca.
const SEARCH_ON_LOAD_PAGE = window.location.href.includes('refinementList')

const AlgoliaFiltersContext = createContext<FiltersContextProps>({
  openPanel: null,
  isSearchActive: false,
  searchOnPageLoad: false,
  isMenuOpen: false,
  setIsSearchActive: () => {},
  setPanel: () => {},
  resetPanel: () => {},
  setIsMenuOpen: () => {},
  getHexColor: () => undefined,
})

interface FilterColorProps {
  name: string
  hex: string
}

interface AlgoliaSearchProviderProps {
  children: ReactNode
}

export const useAlgoliaFilters = () => useContext(AlgoliaFiltersContext)

const appId = $settings.algolia_app_id ?? ''
const searchKey = $settings.algolia_search_key ?? ''
const indexName = $settings.algolia_index_name ?? ''

const algoliaSearchClient = algoliasearch(appId, searchKey)

const filterColorsEl = document.getElementById('collection-filter-colors')
const filterColors: FilterColorProps[] = filterColorsEl
  ? parseJSONSafe(filterColorsEl.innerText)
  : []
const collectionFacetEl = document.getElementById('filter-handles')
const collectionFacet: string[] = collectionFacetEl
  ? parseJSONSafe(collectionFacetEl.innerText)
  : []

const facetString =
  collectionFacet?.map((facet) => `collections.handle:${facet}`).join(' OR ') ||
  ''
// TODO: tirare fuori questa getHexColor dal provider, metterla in utils e testarla
const getHexColor = (colorName: string) =>
  filterColors.find(({ name }) => handleize(name) === handleize(colorName))
    ?.hex || undefined

const inactiveSearchClient = {
  ...algoliaSearchClient,
  // Se here on where to find this type: https://discourse.algolia.com/t/indice-updatedat-type-mismatch/11518
  search(requests: readonly MultipleQueriesQuery[]) {
    return Promise.resolve({
      results: requests.map(() => ({
        hits: [],
        nbHits: 0,
        nbPages: 0,
        processingTimeMS: 0,
        page: 0,
        hitsPerPage: 0,
        exhaustiveNbHits: false,
        query: '',
        params: '',
      })),
    })
  },
}

export const AlgoliaSearchProvider = ({
  children,
}: AlgoliaSearchProviderProps) => {
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false)
  const [openPanel, setOpenPanel] = useState<string | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const setPanel = useCallback(
    (handle: string) => {
      setOpenPanel(handle === openPanel ? null : handle)
    },
    [openPanel, setOpenPanel],
  )

  const resetPanel = () => {
    setOpenPanel(null)
  }

  return (
    // TODO: tipizzare correttamente inactiveSearchClient
    <InstantSearch
      routing
      indexName={indexName}
      searchClient={
        isSearchActive || SEARCH_ON_LOAD_PAGE
          ? algoliaSearchClient
          : inactiveSearchClient
      }
    >
      <AnalyticsMiddleware />
      {!isEmpty(collectionFacet) && <Configure filters={facetString} />}
      <AlgoliaFiltersContext.Provider
        value={{
          isSearchActive,
          searchOnPageLoad: SEARCH_ON_LOAD_PAGE,
          openPanel,
          isMenuOpen,
          setIsSearchActive,
          setPanel,
          resetPanel,
          setIsMenuOpen,
          getHexColor,
        }}
      >
        {children}
      </AlgoliaFiltersContext.Provider>
    </InstantSearch>
  )
}
